import React, { useState } from "react";
import { message } from "antd";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Fade,
  Slide,
  Switch,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import imageCompression from "browser-image-compression";
import { API_BASE_URL, getAuthHeaders, getUploadAuthHeaders } from "../API/Api";
import {
  resolveImageUrl,
  extractUploadedImagePath,
  extractUploadedImageUrl,
} from "./eventUtils";

const slugify = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const PostEvent = () => {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [eventDate, setEventDate] = useState("");
  const [address, setAddress] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleTitleChange = (value) => {
    setTitle(value);
    if (!slug || slug === slugify(title)) {
      setSlug(slugify(value));
    }
  };

  const handleReset = () => {
    setTitle("");
    setSlug("");
    setDescription("");
    setImages([]);
    setPreviewUrls([]);
    setEventDate("");
    setAddress("");
    setIsActive(true);
  };

  const handleImageUpload = async (file) => {
    setUploading(true);
    const localPreview = URL.createObjectURL(file);

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 1024,
        useWebWorker: true,
        initialQuality: 0.85,
        alwaysKeepResolution: true,
      });

      const uploadFormData = new FormData();
      uploadFormData.append("image", compressedFile);

      const uploadRes = await fetch(`${API_BASE_URL}/admin/images/upload`, {
        method: "POST",
        headers: getUploadAuthHeaders(),
        body: uploadFormData,
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed. Status: ${uploadRes.status}`);
      }

      const uploadData = await uploadRes.json();
      const uploadedPath = extractUploadedImagePath(uploadData);

      if (!uploadedPath) {
        throw new Error("Upload response missing file path");
      }

      const uploadedUrl =
        extractUploadedImageUrl(uploadData, uploadedPath) || localPreview;

      setImages((prev) => [...prev, uploadedPath]);
      setPreviewUrls((prev) => [...prev, uploadedUrl]);
      // message.success("Image uploaded");
    } catch (error) {
      console.error("Image upload failed:", error);
      message.error("Failed to upload image");
      URL.revokeObjectURL(localPreview);
    } finally {
      setUploading(false);
    }
  };

  const onChangeUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const file of files) {
      await handleImageUpload(file);
    }
    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    const preview = previewUrls[index];
    if (preview?.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
    setImages((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      message.warning("Please enter event title");
      return;
    }
    if (!slug.trim()) {
      message.warning("Please enter event slug");
      return;
    }
    if (!eventDate) {
      message.warning("Please select event date");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        title: title.trim(),
        slug: slug.trim(),
        description: description.trim(),
        address: address.trim() || null,
        images,
        event_date: eventDate,
        is_active: isActive,
      };

      const res = await fetch(`${API_BASE_URL}/admin/events`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      message.success("Event created successfully");
      window.location.href = "/admin/events";
    } catch (error) {
      console.error("Error creating event:", error);
      message.error("Failed to create event");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1000, mx: "auto", my: 5, px: 2 }}>
        <Slide direction="up" in timeout={400}>
          <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Create Event
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 4,
                  p: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  backgroundColor: "grey.50",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, textAlign: "center" }}
                >
                  Event Images ({images.length})
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  {images.map((img, index) => (
                    <Box
                      key={`${img}-${index}`}
                      sx={{
                        position: "relative",
                        width: 120,
                        height: 120,
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        component="img"
                        src={previewUrls[index] || resolveImageUrl(img)}
                        alt={`Event ${index + 1}`}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(index)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          bgcolor: "rgba(0,0,0,0.5)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  {images.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No images uploaded
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: "center" }}>
                  <input
                    id="event-images-upload"
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={onChangeUpload}
                  />
                  <label htmlFor="event-images-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      disabled={uploading}
                    >
                      {uploading ? "Uploading..." : "Add Images"}
                    </Button>
                  </label>
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    rows={4}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Event Date"
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Event location"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button variant="outlined" onClick={handleReset}>
                      Reset
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => (window.location.href = "/admin/events")}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Slide>
      </Box>
    </Fade>
  );
};

export default PostEvent;
